import React, { useEffect, useState } from "react";
import Grid from "@mui/material/Grid";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import CloseIcon from "@mui/icons-material/Close";
import {
  Autocomplete,
  Box,
  TextField,
  IconButton,
  CircularProgress,
} from "@mui/material";
import { GetMakeModelCombo } from "../../Service/_index";
import { ToastError } from "../../UiComponents/Toaster/Toast";

const vehicleName = (row) =>
  [row?.make, row?.model, row?.vehicleVariant].filter(Boolean).join(" ");

/**
 * The enabled, usable combos as picker options. A display name may repeat, so when
 * name + companies read the same on two records the vehicles tell them apart.
 */
export const comboPickerOptions = (combos) => {
  const options = combos
    .filter((combo) => combo.isEnabled && combo.usable)
    .map((combo) => ({
      _id: combo._id,
      displayName: combo.displayName || "",
      odCompany: combo.odCompany,
      tpCompany: combo.tpCompany,
      label: `${combo.displayName || "-"} (${combo.odCompany || "-"} OD + ${
        combo.tpCompany || "-"
      } TP)`,
      vehicles: `${vehicleName(combo.odRow)} / ${vehicleName(combo.tpRow)}`,
    }));
  return options.map(({ vehicles, ...option }) =>
    options.filter((other) => other.label === option.label).length > 1
      ? { ...option, label: `${option.label} ${vehicles}` }
      : option
  );
};

/**
 * Assign one Combined Make & Model to a user.
 *
 * Like AddRtoCityDrawer this does NOT persist on its own — it hands the row back
 * to AddUser, which submits the whole array with the user. There is no per-user
 * value (no discount): the assignment IS the row. Only enabled, usable combos
 * are offered, and ones already on the user are left out.
 *
 * The user's Online Policy dropdown and Combined Make and Model page then list
 * only these. With none assigned they behave as they always did.
 */
const AddMakeModelComboDrawer = ({ existingRows = [], onSave, onClose }) => {
  const [options, setOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      try {
        const response = await GetMakeModelCombo({});
        if (cancelled) return;
        const rows = Array.isArray(response?.data) ? response.data : [];
        setOptions(comboPickerOptions(rows));
      } catch (e) {
        if (!cancelled) ToastError("Failed to load Combined Make & Model list");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Ones already on this user are left OUT — offering one only to reject it on
  // save wastes the operator's time.
  const selectableOptions = React.useMemo(() => {
    const taken = new Set((existingRows || []).map((row) => String(row.makeModelCombo)));
    return options.filter((option) => !taken.has(String(option._id)));
  }, [options, existingRows]);

  const handleSave = () => {
    setError("");
    if (!selected) {
      setError("Please select a Combined Make & Model");
      return;
    }
    if ((existingRows || []).some((row) => String(row.makeModelCombo) === String(selected._id))) {
      setError("This Combined Make & Model is already in the list");
      return;
    }
    onSave({
      makeModelCombo: selected._id,
      // Kept for display only — the reference above is the source of truth.
      displayName: selected.displayName,
      odCompany: selected.odCompany,
      tpCompany: selected.tpCompany,
    });
  };

  return (
    <Box sx={{ pt: 3, px: 3, pb: 0, minWidth: 480 }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          mb: 2,
        }}
      >
        <Typography sx={{ fontWeight: 600, fontSize: 16 }}>
          Add Combined Make &amp; Model
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={12}>
          <Typography sx={{ mb: 1, fontWeight: 500, fontSize: 13 }}>
            Combined Make &amp; Model *
          </Typography>
          <Autocomplete
            // index.scss sets .MuiOutlinedInput-notchedOutline to transparent
            // globally; the app draws input borders through this class instead.
            className="AutoComplete_InputBox"
            // NOT disablePortal: inside a Dialog the inline popup is clipped by
            // the paper and scrolls the dialog instead of overlaying it.
            options={selectableOptions}
            loading={loading}
            value={selected}
            noOptionsText={
              options.length > 0
                ? "Every Combined Make & Model is already added"
                : "No Combined Make & Model available"
            }
            ListboxProps={{ style: { maxHeight: 260 } }}
            componentsProps={{ popper: { style: { zIndex: 1500 } } }}
            onChange={(e, v) => {
              setSelected(v);
              setError("");
            }}
            getOptionLabel={(option) => option?.label || ""}
            isOptionEqualToValue={(option, value) =>
              String(option?._id) === String(value?._id)
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Combined Make & Model"
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loading ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
          <Typography sx={{ fontSize: 11, color: "#667085", mt: 0.5 }}>
            Once any are assigned, only these are selectable for this user on the
            Online Policy form.
          </Typography>
        </Grid>

        {error && (
          <Grid item xs={12}>
            <Typography sx={{ color: "#d32f2f", fontSize: 13 }}>{error}</Typography>
          </Grid>
        )}
      </Grid>

      <div className="Dialog_Footer" style={{ marginTop: 20 }}>
        <Button onClick={onClose} className="Dialog_Cancel">
          Cancel
        </Button>
        <Button onClick={handleSave} className="Dialog_Save_Exit">
          Add
        </Button>
      </div>
    </Box>
  );
};

export default AddMakeModelComboDrawer;
